import { useEffect, useState } from "react";
import { useBranding } from "../branding";
import { PlanPicker, usePlanCatalog } from "./PlanPicker";

/** Plano pré-selecionado via ?plan=<id> (link vindo do site de vendas). */
function planFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get("plan")?.trim() || "";
  } catch {
    return "";
  }
}

/**
 * Cadastro EXCLUSIVO de novo tenant (onboarding self-service vindo do site de
 * vendas). Rota dedicada `/criar-conta` - separada do `/cadastro` do app (que
 * entra na org do domínio), pra não misturar as funções. Chama
 * POST /api/auth/onboarding, que cria uma org NOVA com a pessoa como admin.
 */
export function OnboardingPage() {
  const { appName, logoDataUrl } = useBranding();
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { plans, trialDays } = usePlanCatalog();
  const trialLabel = trialDays === 1 ? "1 dia grátis" : `${trialDays} dias grátis`;
  const [planId, setPlanId] = useState(() => planFromUrl() || "assistant_essencial");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quando o catálogo carrega: se o ?plan= da URL não existir (ou estava vazio),
  // cai no primeiro plano do catálogo - evita enviar um id inválido.
  useEffect(() => {
    if (plans.length === 0) return;
    if (!plans.some((p) => p.id === planId)) {
      setPlanId(plans[0]?.id ?? "assistant_essencial");
    }
  }, [plans, planId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgName: orgName.trim(), name: name.trim(), email: email.trim(), password, planId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error || "falha ao criar a conta");
        return;
      }
      window.location.href = "/"; // já logado - vai pro app
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page auth-split">
      <aside className="auth-hero" aria-hidden>
        <div className="auth-hero-inner">
          <div className="auth-hero-brand">
            {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
            <span>{appName}</span>
          </div>
          <h1 className="auth-hero-title">Sua IA com o contexto da sua empresa, em minutos.</h1>
          <p className="auth-hero-sub">
            Crie a conta, conecte seus repositórios e comece a perguntar. {trialLabel}, sem cartão.
          </p>
          <ul className="auth-hero-list">
            <li>Documentação automática do seu código</li>
            <li>Respostas de negócio, técnicas e de suporte</li>
            <li>Marca e dados isolados por organização</li>
          </ul>
        </div>
      </aside>
      <div className="auth-pane">
      <div className="auth-card auth-card-wide">
        <div className="auth-brand">
          {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
          <span>{appName}</span>
        </div>
        <p className="auth-sub">Crie sua organização - você vira admin de uma conta nova. {trialLabel}.</p>
        <form className="auth-form" onSubmit={submit}>
          <label className="auth-field">
            <span>Nome da empresa</span>
            <input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Ex.: Minha Empresa Ltda" required autoFocus />
          </label>
          <div className="auth-form-row">
            <label className="auth-field">
              <span>Seu nome</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Como quer ser chamado" />
            </label>
            <label className="auth-field">
              <span>E-mail</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" required />
            </label>
          </div>
          <label className="auth-field">
            <span>Senha</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="mínimo 8 caracteres" minLength={8} required />
          </label>
          <div>
            <span style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Escolha seu plano ({trialLabel})</span>
            <PlanPicker plans={plans} value={planId} onChange={setPlanId} />
          </div>
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? "Criando…" : "Criar conta grátis"}
          </button>
        </form>
        <p className="auth-sub" style={{ textAlign: "center", marginTop: 8 }}>
          Já tem conta? <a href="/login">Entrar</a>
        </p>
      </div>
      </div>
    </div>
  );
}
